import dayjs from 'dayjs';
import React from 'react';
import { AVAILABLE_FEATURES_LIST, PAYMENTS_DATE_FORMAT } from 'src/constants';
import {
  useGetUser,
  useSession,
  useSubscriptionInfo,
  useTeamInfo,
  useTierConfig,
} from 'src/hooks';
import { LabelPro } from 'src/components/LabelPro';
import './SubscriptionPlansForm.scss';
import { LabelFree } from 'src/components/LabelFree';
import { LabelEnterpriseTrial } from 'src/components/LabelEnterpriseTrial';
import { LabelUltra } from 'src/components/LabelUltra';
import { ChangePlanButton } from 'src/components/ChangePlanButton';
import { UpgradeButton } from 'src/components/UpgradeButton';
import LabelStandard from 'src/components/LabelStandard/LabelStandard';
import { LabelAnnual } from 'src/components/LabelAnnual';
import { LabelTeamsPro } from 'src/components/LabelTeamsPro';
import { LabelTeamsStandard } from 'src/components/LabelTeamsStandard';
import { UserTier } from 'src/types/models/UserTier';
import { LabelNumberOfSeats } from 'src/components/LabelNumberOfSeats';
import { LabelSuperNinjaUltra } from 'src/components/LabelSuperNinjaUltra';
import { LabelBusinessMax } from 'src/components/LabelBusinessMax';
import { LabelBusiness } from 'src/components/LabelBusiness';

// TODO(olha): divide to small components
export const SubscriptionPlansForm = () => {
  const { appUser } = useSession();

  const {
    isProTrialTier,
    isFreeTier,
    isEnterpriseTrialTier,
    isSubscribedTier,
    isTeamsTier,
    isEnterpriseTier,
    isTeamsUltraTier,
  } = useGetUser();

  const { teamInfo, isMember, isAdmin } = useTeamInfo();

  const { labelShort } = useTierConfig();

  const { subscriptionInfo } = useSubscriptionInfo();

  const isNewBusinessMaxExists = AVAILABLE_FEATURES_LIST.newBusinessMaxPlan;

  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: subscriptionInfo?.price_info?.currency || 'USD',
  });

  const amountInfo = moneyFormatter.format(
    subscriptionInfo?.price_info?.amount || 0,
  );

  const cancelledDate = dayjs(
    subscriptionInfo?.curr_period_end_timestamp,
  ).format(PAYMENTS_DATE_FORMAT);

  const upcomingPayment = subscriptionInfo?.is_cancelled
    ? 'None'
    : `${amountInfo} on ${cancelledDate}`;

  const renderUpcomingLabel = () => {
    if (subscriptionInfo?.scheduled_plan_name === appUser.tier_id) {
      if (
        isTeamsTier &&
        subscriptionInfo?.seat_count !== teamInfo?.num_seats_total
      ) {
        return (
          <LabelNumberOfSeats
            numberOfSeats={subscriptionInfo?.seat_count || 0}
          />
        );
      }
      return <LabelAnnual />;
    }

    switch (subscriptionInfo?.scheduled_plan_name) {
      case UserTier.PRO:
      case UserTier.PAID:
        return <LabelPro />;
      case UserTier.ULTRA:
        return <LabelUltra />;
      case UserTier.STANDARD:
        return <LabelStandard />;
      case UserTier.TEAMS_PRO:
        return <LabelTeamsPro />;
      case UserTier.TEAMS_ULTRA:
        return <LabelBusiness />;
      case UserTier.TEAMS_SUPERNINJA_ULTRA:
        return <LabelBusinessMax label="Ultra" />;
      case UserTier.BUSINESS_SUPERNINJA_PRO:
        return <LabelBusinessMax label="Pro" />;
      case UserTier.BUSINESS_SUPERNINJA_ULTRA:
        return <LabelBusinessMax label="Ultra" />;
      case UserTier.BUSINESS_SUPERNINJA_MAX:
        return <LabelBusinessMax label="Max" />;
      case UserTier.TEAMS_STANDARD:
        return <LabelTeamsStandard />;
      case UserTier.SUPERNINJA_STANDARD:
        return <LabelSuperNinjaUltra label="Standard" />;
      case UserTier.SUPERNINJA_PRO:
        return <LabelSuperNinjaUltra label="Pro" />;
      case UserTier.SUPERNINJA_ULTRA:
        return <LabelSuperNinjaUltra label="Ultra" />;
      case UserTier.SUPERNINJA_MAX:
        return <LabelSuperNinjaUltra label="Max" />;
      default:
        return null;
    }
  };

  return (
    <div className="nj-subscription-plans-form">
      <div className="nj-subscription-plans-form--container">
        <div className="nj-subscription-plans-form--header-wrapper">
          <p className="nj-subscription-plans-form--header">
            <span className="nj-subscription-plans-form--title">
              Your Ninja subscription
            </span>

            <span className="nj-subscription-plans-form--description">
              {isFreeTier &&
                'Get unlimited access to Ninja’s AI Assistant and all Pro features. Cancel anytime.'}
              {isProTrialTier &&
                'Continue enjoying Pro features - Upgrade now!'}
              {isEnterpriseTrialTier &&
                'Continue enjoying Enterprise features - Upgrade now!'}
              {isSubscribedTier && !isEnterpriseTier && (
                <span> Manage & edit your subscription.</span>
              )}
            </span>
          </p>
        </div>

        {isTeamsTier && (
          <div className="nj-subscription-plans-form--field">
            <p className="nj-subscription-plans-form--label">Workspace name</p>
            <div className="nj-subscription-plans-form--value">
              {teamInfo?.team_name}
            </div>
          </div>
        )}

        <div className="nj-subscription-plans-form--field">
          <p className="nj-subscription-plans-form--label">Plan</p>

          {isEnterpriseTrialTier && (
            <p className="nj-subscription-plans-form--value">
              <LabelEnterpriseTrial />
            </p>
          )}
          {!isEnterpriseTrialTier && !isFreeTier && (
            <div className="nj-subscription-plans-form--value-wrapper">
              {labelShort}
              {subscriptionInfo && subscriptionInfo?.is_cancelled && (
                <div className="nj-subscription-plans-form--cancelled-label">
                  Plan will be canceled on &nbsp;
                  <span className="nj-subscription-plans-form--cancelled-date">
                    {cancelledDate}
                  </span>
                </div>
              )}
            </div>
          )}
          {isFreeTier && (
            <p className="nj-subscription-plans-form--value">
              <LabelFree />
            </p>
          )}
        </div>

        {isSubscribedTier &&
          !isMember &&
          !isAdmin &&
          !isEnterpriseTrialTier && (
            <>
              <div className="nj-subscription-plans-form--field">
                <p className="nj-subscription-plans-form--label">
                  Billing cycle
                </p>
                <div className="nj-subscription-plans-form--billing-cycle-row">
                  <p className="nj-subscription-plans-form--value capitalize">
                    {subscriptionInfo?.interval}
                  </p>
                </div>
              </div>

              {isTeamsTier && !isMember && (
                <div className="nj-subscription-plans-form--field">
                  <p className="nj-subscription-plans-form--label">Seats</p>
                  <div className="nj-subscription-plans-form--value">
                    {teamInfo?.num_seats_total}
                  </div>
                </div>
              )}

              {!isMember && !isAdmin && (
                <div className="nj-subscription-plans-form--field">
                  <p className="nj-subscription-plans-form--label">
                    Upcoming Payment
                  </p>
                  <p className="nj-subscription-plans-form--value">
                    {upcomingPayment}
                  </p>

                  {(subscriptionInfo?.has_scheduled_downgrade ||
                    subscriptionInfo?.has_scheduled_plan_change ||
                    subscriptionInfo?.processing_plan_change) &&
                    !subscriptionInfo?.is_cancelled && (
                      <>{renderUpcomingLabel()}</>
                    )}
                </div>
              )}
            </>
          )}
      </div>

      {!isEnterpriseTier && (isNewBusinessMaxExists || !isTeamsUltraTier) && (
        <div className="nj-subscription-plans-action-buttons-wrapper">
          {!isSubscribedTier || isEnterpriseTrialTier ? (
            <UpgradeButton dataGTM="Subscription-upgrade" />
          ) : (
            <ChangePlanButton />
          )}
        </div>
      )}
    </div>
  );
};
